import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getDashboardStats, getTenants } from '@/services'
import { AppButton, EmptyState, KPICard, Skeleton, formatCount, formatMoney } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'

const mrrTrend = [180, 198, 215, 228, 248, 272, 295, 318, 340, 358, 380, 412]
const tenantsTrend = [42, 46, 51, 55, 60, 67, 74, 82, 91, 98, 104, 112]

function MRRChart({ data }: { data: number[] }) {
  const width = 720
  const height = 220
  const pad = { top: 12, right: 12, bottom: 22, left: 36 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const max = Math.max(...data) * 1.08
  const step = innerW / (data.length - 1)
  const months = ['Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic', 'Ene', 'Feb', 'Mar', 'Abr', 'May']
  const pts = data.map((value, index) => [pad.left + index * step, pad.top + innerH - (value / max) * innerH])
  const linePath = pts.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point[0]} ${point[1]}`).join(' ')
  const areaPath = `${linePath} L ${pts[pts.length - 1][0]} ${pad.top + innerH} L ${pts[0][0]} ${pad.top + innerH} Z`
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((tick) => ({ value: max * tick, y: pad.top + innerH - tick * innerH }))

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
      {yTicks.map((tick, index) => <line key={index} x1={pad.left} x2={width - pad.right} y1={tick.y} y2={tick.y} stroke="#E5DED1" strokeDasharray="2 4" />)}
      <path d={areaPath} fill="#15B868" opacity="0.1" />
      <path d={linePath} fill="none" stroke="#15B868" strokeWidth="2" />
      {pts.map((point, index) => <circle key={index} cx={point[0]} cy={point[1]} r="2.5" fill="#FFFFFF" stroke="#15B868" strokeWidth="1.5" />)}
      {months.map((month, index) => <text key={month} x={pad.left + index * step} y={height - 6} fontSize="10" fill="#7A847F" textAnchor="middle" fontFamily="DM Sans">{month}</text>)}
      {yTicks.map((tick, index) => <text key={index} x={pad.left - 6} y={tick.y + 3} fontSize="10" fill="#7A847F" textAnchor="end" fontFamily="DM Sans">${Math.round(tick.value)}K</text>)}
    </svg>
  )
}

function Donut({ data }: { data: Array<{ label: string; value: number; color: string }> }) {
  const size = 140
  const total = data.reduce((sum, item) => sum + item.value, 0) || 1
  const radius = size / 2
  const inner = radius - 22

  // El angulo de cada porcion depende de la suma de las anteriores. Se acumula
  // dentro del reduce en vez de mutar una variable del render: asi el calculo
  // da lo mismo las veces que React vuelva a renderizar.
  const slices = data.reduce<Array<{ item: (typeof data)[number]; startValue: number; endValue: number }>>(
    (acc, item) => {
      const startValue = acc.length > 0 ? acc[acc.length - 1].endValue : 0
      return [...acc, { item, startValue, endValue: startValue + item.value }]
    },
    [],
  )

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map(({ item, startValue, endValue }) => {
        const start = (startValue / total) * 2 * Math.PI - Math.PI / 2
        const end = (endValue / total) * 2 * Math.PI - Math.PI / 2
        const x1 = radius + radius * Math.cos(start)
        const y1 = radius + radius * Math.sin(start)
        const x2 = radius + radius * Math.cos(end)
        const y2 = radius + radius * Math.sin(end)
        const xi1 = radius + inner * Math.cos(end)
        const yi1 = radius + inner * Math.sin(end)
        const xi2 = radius + inner * Math.cos(start)
        const yi2 = radius + inner * Math.sin(start)
        const large = end - start > Math.PI ? 1 : 0
        const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi2} ${yi2} Z`
        return <path key={item.label} d={path} fill={item.color} />
      })}
      <text x={radius} y={radius - 4} textAnchor="middle" fontFamily="Bricolage Grotesque" fontWeight="700" fontSize="22" fill="#0F1C14">{total}</text>
      <text x={radius} y={radius + 14} textAnchor="middle" fontFamily="DM Sans" fontSize="11" fill="#7A847F">tenants</text>
    </svg>
  )
}

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useQuery({ queryKey: ['dashboard-stats'], queryFn: getDashboardStats })
  const { data: tenantsResult, isLoading: tenantsLoading } = useQuery({ queryKey: ['dashboard-tenants'], queryFn: () => getTenants({ pageSize: 50 }) })
  const isInitialLoading = statsLoading || tenantsLoading
  const tenants = tenantsResult?.data || []
  const totalTenants = stats?.totalTenants || tenants.length
  const activeTenants = stats?.activeTenants || tenants.filter((tenant) => tenant.subscription_status === 'active').length
  const trialingTenants = stats?.trialingTenants || tenants.filter((tenant) => tenant.subscription_status === 'trialing').length
  const newThisMonth = stats?.newTenantsThisMonth || 0
  const monthlyRevenue = stats?.monthlyRevenue || stats?.totalRevenue || 0
  const pastDueTenants = tenants.filter((tenant) => tenant.subscription_status === 'past_due')
  const planDistribution = (stats?.planDistribution || []).map((item) => ({
    label: item.plan,
    value: item.count,
    color: ({ business: '#0F1C14', pro: '#15B868', starter: '#A8D5B8', trial: '#F9C840' } as Record<string, string>)[item.plan] || '#E8E1D2',
  })).filter((item) => item.value > 0)
  const topTenants = [...tenants].sort((a, b) => (b.current_orders_month || 0) - (a.current_orders_month || 0)).slice(0, 5)
  const maxOrders = Math.max(...topTenants.map((tenant) => tenant.current_orders_month || 0), 1)

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Buenos dias, Equipo Bisne</div>
          <h1>Operaciones SaaS</h1>
          <div className="subtitle">{isInitialLoading ? <Skeleton width={300} height={14} /> : `${activeTenants} tenants activos · ${pastDueTenants.length} alertas requieren atencion`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
          <Link to="/tenants/new"><AppButton variant="primary" icon="plus">Nuevo tenant</AppButton></Link>
        </div>
      </div>

      <div className="kpi-grid">
        <KPICard label="MRR" value={monthlyRevenue ? formatMoney(monthlyRevenue) : '$0'} unit="ARS" trend="+8.4% vs abril" spark={mrrTrend} loading={isInitialLoading} feature />
        <KPICard label="Tenants totales" value={totalTenants} trend={`+${newThisMonth} este mes`} spark={tenantsTrend} loading={isInitialLoading} />
        <KPICard label="Activos" value={activeTenants} trend={`${totalTenants ? Math.round((activeTenants / totalTenants) * 100) : 0}%`} loading={isInitialLoading} />
        <KPICard label="En trial" value={trialingTenants} trend="pipeline activo" loading={isInitialLoading} />
        <KPICard label="Nuevos este mes" value={newThisMonth} trend="alta reciente" loading={isInitialLoading} />
        <KPICard label="Churn 30d" value={(stats?.churnRate || 0).toFixed(1)} unit="%" trend="controlado" loading={isInitialLoading} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginTop: 16 }}>
        <div className="card">
          <div className="card-header">
            <div>
              <h3>MRR — ultimos 12 meses</h3>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>En miles de ARS</div>
            </div>
            <div className="row"><span className="filter-chip active">12M</span><span className="filter-chip">6M</span><span className="filter-chip">3M</span></div>
          </div>
          <div className="card-body">{isInitialLoading ? <Skeleton height={220} /> : <MRRChart data={mrrTrend} />}</div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Distribucion por plan</h3></div>
          <div className="card-body">
            {isInitialLoading
              ? <Skeleton height={176} />
              : planDistribution.length ? <div className="donut-wrap"><Donut data={planDistribution} /><div className="donut-legend">{planDistribution.map((item) => <div key={item.label} className="donut-legend-item"><span className="swatch" style={{ background: item.color }} /><span className="label">{item.label}</span><span className="value">{item.value}</span></div>)}</div></div> : <EmptyState icon="layers" title="Sin planes" desc="Todavia no hay tenants asociados a planes." />}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 16, marginTop: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Alertas ({pastDueTenants.length})</h3><Link to="/subscriptions" style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 700 }}>Ver todas →</Link></div>
          <div className="card-body flush">
            {isInitialLoading ? <Skeleton height={180} /> : pastDueTenants.length === 0 ? <EmptyState icon="check" title="Todo en orden" desc="No hay alertas activas en este momento." /> : pastDueTenants.slice(0, 6).map((tenant) => <Link key={tenant.id} className="alert-row" to={`/tenants/${tenant.id}`}><div className="icon-wrap warning"><Icon name="card" size={14} /></div><div className="text"><div className="title">{tenant.business_name} con pago pendiente</div><div className="desc">Subscription en past_due · revisar billing</div></div><Icon name="chevronRight" size={14} /></Link>)}
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>Top tenants por uso</h3><span className="badge badge-outline">Pedidos 30d</span></div>
          <div className="card-body">
            <div className="bar-chart">
              {isInitialLoading ? <Skeleton height={160} /> : topTenants.map((tenant) => {
                const orders = tenant.current_orders_month || 0
                return <Link key={tenant.id} className="bar-row" to={`/tenants/${tenant.id}`}><div className="label">{tenant.business_name.length > 16 ? `${tenant.business_name.slice(0, 16)}...` : tenant.business_name}</div><div className="track"><div className="fill" style={{ width: `${(orders / maxOrders) * 100}%` }} /></div><div className="value">{formatCount(orders)}</div></Link>
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
